import { Component } from 'react';
import Portal from 'components/Shared/Portal';
import styles from './styles.scss';
import cn from 'classnames';
import { isBrowser, NO_ELEMENT } from 'utils';
import Button from 'components/Shared/Button';
import layoutStyles from 'components/Pagelayout/styles.scss';
import warning from 'warning';
import { HEADER_TOP_BAR_ID } from 'components/Header';

type CloseEvent = React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>;
export interface OwnProps {
  show: boolean;
  disableOverlay?: boolean;

  overlayClassName?: string;
  contentClassName?: string;
  contentStyle?: React.CSSProperties;

  onClose?: (event: CloseEvent) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLElement>) => void;
  containerSelector?: () => HTMLElement | null;

  children?: React.ReactNode;
  showDefaultButton?: boolean;
}

const ESC_KEY = 'Escape';

const MODAL_ID = 'modal';
export const getCurrentModalElement = () => {
  if (!isBrowser()) {
    return undefined;
  }

  const elem = document.getElementById(MODAL_ID);

  return elem ? elem : undefined;
};

export const getScrollbarWidth = () => {
  if (!isBrowser()) {
    warning(true, 'Cannot calculate the scrollbar width on the server.');
    return 0;
  }
  const documentWidth = document.documentElement.clientWidth;
  const windowWidth = window.innerWidth;
  const scrollBarWidth = windowWidth - documentWidth;
  return scrollBarWidth;
};

export default class Modal extends Component<OwnProps> {
  private refContent: HTMLDivElement;
  private shouldClose: boolean | undefined;
  private shouldFocus: boolean | undefined;
  private scrollbarWidth: number;

  public componentDidMount() {
    this.scrollbarWidth = getScrollbarWidth();
    if (this.props.show) {
      const topBar = document.getElementById(HEADER_TOP_BAR_ID);
      if (topBar) {
        topBar.style.marginRight = `${this.scrollbarWidth}px`;
      }
      document.body.classList.add(layoutStyles.lockBody);
      document.body.style.marginRight = `${this.scrollbarWidth}px`;

      this.setFocus();
      this.shouldFocus = false;
    }
  }

  public UNSAFE_componentWillReceiveProps(newProps: OwnProps) {
    if (!this.props.show && newProps.show) {
      const topBar = document.getElementById(HEADER_TOP_BAR_ID);
      if (topBar) {
        topBar.style.marginRight = `${this.scrollbarWidth}px`;
      }
      document.body.classList.add(layoutStyles.lockBody);
      document.body.style.marginRight = `${this.scrollbarWidth}px`;

      this.shouldFocus = true;
    }
    if (this.props.show && !newProps.show) {
      document.body.classList.remove(layoutStyles.lockBody);
      const topBar = document.getElementById(HEADER_TOP_BAR_ID);
      document.body.style.marginRight = '';
      if (topBar) {
        topBar.style.marginRight = '';
      }
    }
  }

  public componentWillUnmount() {
    const topBar = document.getElementById(HEADER_TOP_BAR_ID);
    document.body.classList.remove(layoutStyles.lockBody);
    if (topBar) {
      topBar.style.marginRight = '';
    }
    document.body.style.marginRight = '';
  }

  public componentDidUpdate() {
    if (this.shouldFocus) {
      this.setFocus();
      this.shouldFocus = false;
    }
  }

  public render() {
    const {
      containerSelector,
      overlayClassName,
      contentClassName,
      children,
      showDefaultButton,
      contentStyle
    } = this.props;

    return !this.shouldShow() ? (
      NO_ELEMENT
    ) : (
      <Portal container={containerSelector}>
        <div
          id={MODAL_ID}
          className={cn(overlayClassName, styles.overlay)}
          onClick={this.onOverlayClick}
        >
          <div
            tabIndex={-1}
            className={cn(contentClassName, styles.content)}
            style={contentStyle}
            onClick={this.onContentClick}
            onKeyDown={this.onKeyDown}
            ref={this.setContent}
          >
            {children}
            {showDefaultButton && (
              <Button onClick={this.props.onClose} className={styles.modalButton}>
                Stäng
              </Button>
            )}
          </div>
        </div>
      </Portal>
    );
  }

  private shouldShow = () => isBrowser() && this.props.show;

  private onOverlayClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();

    const event = e;

    if (this.shouldClose === undefined) {
      this.shouldClose = true;
    }
    if (this.shouldClose && !this.props.disableOverlay && !!this.props.onClose) {
      this.props.onClose(event);
    }

    window?.onclick?.(event.nativeEvent);

    this.shouldClose = undefined;
  };

  private onContentClick = () => {
    this.shouldClose = false;
  };

  private onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === ESC_KEY && this.props.onClose) {
      e.stopPropagation();
      this.props.onClose(e);
    }
    this.props.onKeyDown && this.props.onKeyDown(e);
  };

  private hasFocus = () =>
    document.activeElement === this.refContent || this.refContent.contains(document.activeElement);

  private setFocus = () => this.refContent && !this.hasFocus() && this.refContent.focus();

  private setContent = (content: HTMLDivElement) => (this.refContent = content);
}
